/* eslint-disable @typescript-eslint/no-floating-promises */
import { showNotification } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { customAxios } from "lib/axios";

type UpdateOvertimeData = {
  recordId: string;
  isOvertimeApproved: boolean;
};

type AttendanceRecord = {
  recordId: string;
  employee?: {
    id: string;
  };
  isOvertimeApproved: boolean;
};

type AttendanceResponse = {
  data?: {
    records?: AttendanceRecord[];
  };
};

type OvertimeResponse = {
  success: boolean;
  message: string;
  data: {
    employeeId: string;
    recordId: string;
    isOvertimeApproved: boolean;
    updatedAt: string;
  };
};

const updateOvertimeStatus = async ({
  recordId,
  isOvertimeApproved,
}: UpdateOvertimeData): Promise<OvertimeResponse> => {
  const { data } = await customAxios.patch<OvertimeResponse>(
    `/attendance/overtime-approval/${recordId}`,
    {
      isOvertimeApproved,
    }
  );
  return data;
};

export const useUpdateOvertimeStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateOvertimeStatus,

    onMutate: async (variables: UpdateOvertimeData) => {
      await queryClient.cancelQueries({ queryKey: ["attendanceAdminList"] });

      const previousAttendanceData =
        queryClient.getQueryData<AttendanceResponse>(["attendanceAdminList"]);

      queryClient.setQueryData<AttendanceResponse>(
        ["attendanceAdminList"],
        (old) => {
          if (!old?.data?.records) return old;

          return {
            ...old,
            data: {
              ...old.data,
              records: old.data.records.map((record) =>
                record.recordId === variables.recordId
                  ? {
                      ...record,
                      isOvertimeApproved: variables.isOvertimeApproved,
                    }
                  : record
              ),
            },
          };
        }
      );

      return { previousAttendanceData };
    },

    onSuccess: () => {
      showNotification({
        title: "Success",
        message: "Overtime approved successfully!",
        color: "green",
      });

      queryClient.invalidateQueries({ queryKey: ["attendanceAdminList"] });
    },

    onError: (err, _vars, context) => {
      if (context?.previousAttendanceData) {
        queryClient.setQueryData(
          ["attendanceAdminList"],
          context.previousAttendanceData
        );
      }

      showNotification({
        title: "Error",
        message: "Failed to approve overtime. Please try again.",
        color: "red",
      });

      console.error("Overtime approval error:", err);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["attendanceAdminList"] });
    },
  });
};
